import type { ReactNode, SVGProps } from 'react'

/*
  The console's icon set, ported one-for-one from the design prototype's sprite
  (console.html, `<symbol id="i-…">`). Same viewBox, paths and stroke attributes;
  the name is the symbol id without its `i-` prefix. Add an icon here only when the
  design adds one. The onboarding prototype adds the last eleven (`id` … `flagau`),
  copied from its own sprite the same way; where it redraws one the set already has
  (back, card, doc …), the set's own is used.
*/

export type IconName =
  | 'home'
  | 'bot'
  | 'phone'
  | 'cal'
  | 'hash'
  | 'chart'
  | 'book'
  | 'route'
  | 'gear'
  | 'card'
  | 'help'
  | 'doc'
  | 'search'
  | 'bell'
  | 'plus'
  | 'play'
  | 'pause'
  | 'stop'
  | 'replay'
  | 'chev'
  | 'x'
  | 'check'
  | 'warn'
  | 'clock'
  | 'arrow'
  | 'left'
  | 'right'
  | 'transfer'
  | 'mic'
  | 'menu'
  | 'sliders'
  | 'theme'
  | 'dl'
  | 'dots'
  | 'flag'
  | 'spark'
  | 'tagic'
  | 'sun'
  | 'moon'
  | 'user'
  | 'gift'
  | 'switch'
  | 'logout'
  | 'id'
  | 'lock'
  | 'mail'
  | 'info'
  | 'msg'
  | 'copy'
  | 'globe'
  | 'upload'
  | 'google'
  | 'ms'
  | 'flagau'

const ICONS: Record<IconName, { viewBox: string; body: ReactNode }> = {
  home: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M3 10.5 12 3l9 7.5V21h-6v-6h-6v6H3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </>
    ),
  },
  bot: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <rect x="4" y="7" width="16" height="12" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M12 7V3M8 3h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
        <circle cx="9" cy="13" r="1.3" fill="currentColor" />
        <circle cx="15" cy="13" r="1.3" fill="currentColor" />
      </>
    ),
  },
  phone: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M5 4h4l2 5-2.5 1.5a12 12 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </>
    ),
  },
  cal: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  hash: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M9 4 7 20M17 4l-2 16M4 9h17M3 15h17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  chart: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M4 20V10M10 20V4M16 20v-8M22 20H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  book: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M4 5a2 2 0 0 1 2-2h14v18H6a2 2 0 0 0-2 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M4 19a2 2 0 0 1 2-2h14" fill="none" stroke="currentColor" strokeWidth="2" />
      </>
    ),
  },
  route: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M4 7h7a4 4 0 0 1 4 4v2a4 4 0 0 0 4 4h1M4 17h5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="m17 4 3 3-3 3M17 14l3 3-3 3" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  gear: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M12 2v3m0 14v3M2 12h3m14 0h3M4.9 4.9l2.1 2.1m10 10 2.1 2.1m0-14.2-2.1 2.1m-10 10-2.1 2.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  card: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <rect x="2" y="5" width="20" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M2 10h20" stroke="currentColor" strokeWidth="2" />
      </>
    ),
  },
  help: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M9.5 9.5a2.5 2.5 0 1 1 3.4 2.3c-.8.3-.9 1-.9 1.7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="17" r="1.1" fill="currentColor" />
      </>
    ),
  },
  doc: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M6 2h8l4 4v16H6z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M14 2v4h4M9 11h6M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  search: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="m16.5 16.5 4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  bell: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M10 20a2.2 2.2 0 0 0 4 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  plus: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      </>
    ),
  },
  play: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <polygon points="7 4 20 12 7 20" fill="currentColor" />
      </>
    ),
  },
  pause: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M8 5v14M16 5v14" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </>
    ),
  },
  stop: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
      </>
    ),
  },
  replay: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M4 10a8 8 0 1 1 2 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 4v6h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  chev: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  x: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  check: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="m4 12.5 5.5 5.5L20 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  warn: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M12 3 2.5 20h19z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M12 9v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="17" r="1.1" fill="currentColor" />
      </>
    ),
  },
  clock: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M12 7v5l3.5 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  arrow: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M4 12h15M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  left: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="m14 6-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  right: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="m10 6 6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  transfer: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M4 8h13M13 4l4 4-4 4M20 16H7M11 12l-4 4 4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  mic: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <rect x="9" y="3" width="6" height="11" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M5 11a7 7 0 0 0 14 0M12 18v3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  menu: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  sliders: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M4 7h10m4 0h2M4 12h4m4 0h8M4 17h12m4 0h0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="16" cy="7" r="2" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="10" cy="12" r="2" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="18" cy="17" r="2" fill="none" stroke="currentColor" strokeWidth="2" />
      </>
    ),
  },
  theme: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" />
      </>
    ),
  },
  dl: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M12 4v11m-5-4 5 5 5-5M4 20h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  dots: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="5" cy="12" r="1.7" fill="currentColor" />
        <circle cx="12" cy="12" r="1.7" fill="currentColor" />
        <circle cx="19" cy="12" r="1.7" fill="currentColor" />
      </>
    ),
  },
  flag: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M5 21V4m0 1h12l-2.5 4L17 13H5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  spark: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M12 2l2.2 6.4L21 11l-6.8 2.6L12 20l-2.2-6.4L3 11l6.8-2.6z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </>
    ),
  },
  tagic: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M3 12V3h9l9 9-9 9z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <circle cx="8" cy="8" r="1.6" fill="currentColor" />
      </>
    ),
  },
  sun: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  moon: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4 8.5 8.5 0 1 0 20 14.5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </>
    ),
  },
  user: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M4 21a8 8 0 0 1 16 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  gift: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <rect x="3" y="8" width="18" height="5" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M5 13v8h14v-8M12 8v13M8.5 8C6.5 8 5.5 6.5 6 5s2.5-1.5 3.8 0c.9 1 .7 3 .7 3zM15.5 8c2 0 3-1.5 2.5-3s-2.5-1.5-3.8 0c-.9 1-.7 3-.7 3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </>
    ),
  },
  switch: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M16 3h5v5M21 3l-7 7M8 21H3v-5M3 21l7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  logout: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  // From the onboarding prototype's sprite.
  id: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="9" cy="11" r="2" />
        <path d="M6 16c.6-1.5 1.8-2 3-2s2.4.5 3 2M15 10h3M15 14h3" />
      </g>
    ),
  },
  lock: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </g>
    ),
  },
  mail: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 7l9 6 9-6" />
      </g>
    ),
  },
  info: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 8h.01" />
      </g>
    ),
  },
  msg: {
    viewBox: '0 0 24 24',
    body: <path d="M4 5h16v11H9l-5 4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />,
  },
  copy: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="9" y="9" width="11" height="11" rx="2" />
        <path d="M5 15V5a1 1 0 0 1 1-1h9" />
      </g>
    ),
  },
  globe: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
      </g>
    ),
  },
  upload: {
    viewBox: '0 0 24 24',
    body: (
      <path
        d="M12 15V4M7 9l5-5 5 5M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
  },
  google: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path fill="#4285F4" d="M22.6 12.2c0-.8-.1-1.4-.2-2H12v3.9h6c-.1 1-.8 2.5-2.3 3.5l3.6 2.8c2.1-2 3.3-4.8 3.3-8.2z" />
        <path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.8c-.9.7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.5l-3.7 2.9C3.9 20.6 7.6 23 12 23z" />
        <path fill="#FBBC05" d="M5.8 14.1c-.2-.7-.4-1.4-.4-2.1s.1-1.5.4-2.1L2.1 7C1.4 8.5 1 10.2 1 12s.4 3.5 1.1 5l3.7-2.9z" />
        <path fill="#EA4335" d="M12 5.4c2 0 3.4.9 4.2 1.6l3.1-3C17.5 2.3 15 1 12 1 7.6 1 3.9 3.4 2.1 7l3.7 2.9C6.7 7.3 9.1 5.4 12 5.4z" />
      </>
    ),
  },
  ms: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path fill="#F25022" d="M2 2h9.5v9.5H2z" />
        <path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z" />
        <path fill="#00A4EF" d="M2 12.5h9.5V22H2z" />
        <path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z" />
      </>
    ),
  },
  flagau: {
    viewBox: '0 0 20 14',
    body: (
      <>
        <rect width="20" height="14" fill="#012169" />
        <path d="M0 0l10 7M10 0L0 7" stroke="#fff" strokeWidth="1.6" />
        <path d="M0 0l10 7M10 0L0 7" stroke="#C8102E" strokeWidth=".7" />
        <path d="M5 0v7M0 3.5h10" stroke="#fff" strokeWidth="2.2" />
        <path d="M5 0v7M0 3.5h10" stroke="#C8102E" strokeWidth="1.2" />
        <g fill="#fff">
          <circle cx="5" cy="10.6" r="1.3" />
          <circle cx="15.2" cy="3" r=".7" />
          <circle cx="13" cy="6.6" r=".7" />
          <circle cx="17.4" cy="6" r=".7" />
          <circle cx="15.2" cy="11.2" r=".8" />
        </g>
      </>
    ),
  },
}

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children' | 'viewBox'> {
  name: IconName
  /** Gives the icon an accessible name. Without it the icon is decorative and hidden. */
  title?: string
}

/**
 * One icon from the console set. Sized by CSS like the prototype (`.nav-btn svg`,
 * `.btn svg` …) or by a class; 1em square when nothing sizes it.
 */
export function Icon({ name, title, width = '1em', height = '1em', ...svgProps }: IconProps) {
  const icon = ICONS[name]
  return (
    <svg
      viewBox={icon.viewBox}
      width={width}
      height={height}
      focusable="false"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...svgProps}
    >
      {title ? <title>{title}</title> : null}
      {icon.body}
    </svg>
  )
}
